import { useRef } from 'react'
import { toast } from 'sonner'
import { usePortal } from '@/contexts/PortalContext'
import { PortalApiError } from '@/lib/portalApi'
import { ExtractionProgressBar } from './ExtractionProgressBar'
import { AIExtractionTable } from './AIExtractionTable'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 Mo

function getQualityLabel(score: number): string {
  if (score >= 0.85) return 'excellent'
  if (score >= 0.60) return 'bon'
  return 'partiel'
}

export function DocumentUploadZone() {
  const { uploadDocument, extractionStatus, extractions, documentQualityScore } = usePortal()
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    // 9.5 — Validation côté client avant l'envoi
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Le fichier ne doit pas dépasser 10 Mo', { duration: Infinity })
      return
    }

    try {
      await uploadDocument(file)
    } catch (e: unknown) {
      if (e instanceof PortalApiError && e.status === 413) {
        // 9.9 — HTTP 413 du serveur (double-check)
        toast.error('Le fichier ne doit pas dépasser 10 Mo', { duration: Infinity })
      } else {
        toast.error("Erreur lors de l'extraction. Veuillez réessayer.", { duration: Infinity })
      }
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) void handleFile(file)
    // Reset input pour permettre le re-upload du même fichier
    e.target.value = ''
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) void handleFile(file)
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
  }

  const isUploading = extractionStatus === 'uploading'
  const isDone = extractionStatus === 'done'
  const isQuotaExceeded = extractionStatus === 'quota_exceeded'

  return (
    <div className="mb-4 border rounded-lg p-4 bg-muted/30 space-y-3">
      {/* CA-1 — Notice de confidentialité (UX-6) */}
      <p className="text-xs text-muted-foreground">
        🔒 Votre document est traité en mémoire et n'est pas transmis à votre client — seules les valeurs extraites sont partagées.
      </p>

      {/* CA-1 — Exemples de documents */}
      <p className="text-xs text-muted-foreground">
        Exemples : fiches techniques PDF, relevés de consommation Excel, bilans carbone internes
      </p>

      {/* CA-3 — Quota épuisé : masquer l'input et afficher le message */}
      {isQuotaExceeded ? (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Le quota d'extraction IA de votre client est épuisé. Veuillez saisir vos données manuellement.
        </p>
      ) : isUploading ? (
        /* CA-5 — Animation en cours : masquer le bouton */
        <ExtractionProgressBar status={extractionStatus} />
      ) : isDone ? (
        /* CA-5 — Extraction terminée */
        <>
          <ExtractionProgressBar status="done" extractionCount={extractions.length} />
          {/* CA-5 / UX-2 — Score qualité document */}
          {documentQualityScore !== null && (
            <p className="text-sm text-muted-foreground mt-2">
              Votre document était{' '}
              <strong>{getQualityLabel(documentQualityScore)}</strong>
              {' — '}{Math.round(documentQualityScore * 100)}% des champs ont été pré-remplis automatiquement
            </p>
          )}
          {/* CA-1 — Table de validation des extractions IA (UX-12) */}
          {extractions.length > 0 && (
            <div className="mt-4">
              <AIExtractionTable />
            </div>
          )}
        </>
      ) : (
        /* Zone d'upload normale */
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-muted-foreground/30 rounded p-4 text-center cursor-pointer hover:border-muted-foreground/60 transition-colors"
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
          aria-label="Zone d'upload de document"
        >
          <p className="text-sm text-muted-foreground">
            Glissez un fichier ici ou{' '}
            <span className="underline">parcourez</span>
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">PDF, Excel (.xlsx) — max 10 Mo</p>
        </div>
      )}

      {/* Input caché — CA-2 */}
      {!isQuotaExceeded && (
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.xlsx"
          className="hidden"
          onChange={handleChange}
          aria-label="Sélectionner un fichier à extraire"
        />
      )}
    </div>
  )
}
